import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, CartesianGrid } from "recharts";
import { DollarSign, ShoppingBag, Eye, Mail, TrendingUp, Clock } from "lucide-react";
import api from "@/lib/api";

const fmtTime = (s) => {
  s = Math.round(s || 0);
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

const Metric = ({ icon: Icon, label, value, color, testid }) => (
  <div className={`brut border-ink p-5 shadow-brut ${color}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="font-display font-semibold text-sm">{label}</span>
      <Icon className="w-5 h-5" />
    </div>
    <div className="font-display font-bold text-3xl mt-2">{value}</div>
  </div>
);

export default function Overview() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="font-display text-xl">Loading analytics…</div>;
  const s = data.sales, v = data.visitors;

  return (
    <div data-testid="admin-overview">
      <h1 className="font-display font-bold text-3xl mb-1">Overview</h1>
      <p className="text-ink/60 mb-6">Your shop at a glance.</p>

      <div className="mb-4">
        <h2 className="font-display font-bold text-lg mb-2">Sales</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Metric icon={DollarSign} label="Today" value={`$${s.day.toFixed(2)}`} color="bg-sun" testid="metric-sales-day" />
          <Metric icon={DollarSign} label="This week" value={`$${s.week.toFixed(2)}`} color="bg-sky" testid="metric-sales-week" />
          <Metric icon={DollarSign} label="This month" value={`$${s.month.toFixed(2)}`} color="bg-bubble/40" testid="metric-sales-month" />
          <Metric icon={DollarSign} label="This year" value={`$${s.year.toFixed(2)}`} color="bg-tangerine/40" testid="metric-sales-year" />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Metric icon={TrendingUp} label="Total revenue" value={`$${data.total_paid_revenue.toFixed(2)}`} color="bg-white" testid="metric-revenue" />
        <Metric icon={ShoppingBag} label="Orders (paid/total)" value={`${data.total_orders - data.pending_orders}/${data.total_orders}`} color="bg-white" testid="metric-orders" />
        <Metric icon={Eye} label="Total visitors" value={v.total} color="bg-white" testid="metric-visitors" />
        <Metric icon={Clock} label="Avg. time on site" value={fmtTime(data.avg_time_seconds)} color="bg-white" testid="metric-avg-time" />
      </div>

      <div className="brut-card p-5 mb-6" data-testid="recent-visitors">
        <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Clock className="w-5 h-5" /> Recent visitors — time spent</h3>
        {(!data.recent_visitors || data.recent_visitors.length === 0) ? (
          <p className="text-ink/60 text-sm">No visitor sessions recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b-2 border-ink">
                <th className="py-2 font-display">Visited</th>
                <th className="py-2 font-display">Time on site</th>
                <th className="py-2 font-display">Pages viewed</th>
              </tr></thead>
              <tbody>
                {data.recent_visitors.map((rv, i) => (
                  <tr key={i} className={i % 2 ? "bg-cream-solid" : ""} data-testid={`visitor-row-${i}`}>
                    <td className="py-2">{rv.started_at_local || (rv.started_at ? new Date(rv.started_at).toLocaleString() : "—")}</td>
                    <td className="py-2 font-bold">{fmtTime(rv.seconds)}</td>
                    <td className="py-2">{rv.page_views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="brut-card p-5">
          <h3 className="font-display font-bold text-lg mb-4">Sales — last 30 days</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.sales_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#0F172A22" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="sales" fill="#FF66C4" stroke="#0F172A" strokeWidth={2} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="brut-card p-5">
          <h3 className="font-display font-bold text-lg mb-4">Visitors — last 30 days</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data.visit_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#0F172A22" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Area dataKey="visits" fill="#5CE1E6" stroke="#0F172A" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {["day", "week", "month", "year"].map((p) => (
          <div key={p} className="brut border-ink p-4 bg-white shadow-brut" data-testid={`metric-visitors-${p}`}>
            <div className="text-xs font-semibold uppercase text-ink/50">Visitors ({p})</div>
            <div className="font-display font-bold text-2xl">{v[p]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
